import { z } from 'zod';

// WHY (#2314): read contract for `GET /leads/:id/artifacts` — the flat,
// PII-safe list of every artifact (China request, Russia request, Deal,
// external intake submission) linked to a Lead, newest first. The endpoint
// resolves Lead visibility FIRST (404 on an invisible/unknown parent), then
// filters each child kind by its own aggregate's visibility predicate, so a
// cross-owner child never surfaces. Each entity carries only reference
// fields — an id, an optional public code, a kind tag, and a date — never a
// body-field snapshot.

/**
 * @aiPurpose one artifact linked to a Lead. `kind` names the source
 *   aggregate; `id` is the row primary key; `code` is the human public code
 *   when the row has one (China/Russia requests use their PK as the code,
 *   Deals carry `publicCode`, intake submissions have none → `null`); `date`
 *   is the row's `createdAt`.
 * @aiInvariant `kind` is an OPEN `z.string()`, deliberately NOT a `z.enum`.
 *   `apps/web/src/api/client.ts` runs `schema.parse(body)` on every response,
 *   so a closed enum baked into a deployed client would THROW the moment
 *   #2311 (or any later slice) emits a new kind such as `proposal`. Unknown
 *   future kinds MUST parse; `KnownLeadArtifactKindSchema` below is the
 *   closed set a caller narrows against when it needs the known kinds.
 */
export const LeadArtifactEntitySchema = z
  .object({
    kind: z.string().min(1),
    id: z.string().min(1),
    code: z.string().min(1).nullable(),
    date: z.string().datetime(),
  })
  .strict();
export type LeadArtifactEntity = z.infer<typeof LeadArtifactEntitySchema>;

/**
 * @aiPurpose response for `GET /leads/:id/artifacts` (#2314) — the
 *   actor-visible artifacts linked to a Lead the actor can already see,
 *   sorted newest first.
 */
export const GetLeadArtifactsResponseSchema = z
  .object({
    entities: z.array(LeadArtifactEntitySchema),
  })
  .strict();
export type GetLeadArtifactsResponse = z.infer<typeof GetLeadArtifactsResponseSchema>;

/**
 * @aiPurpose the closed set of artifact kinds this backend currently emits.
 *   A reader narrows an open `kind` string against this to branch on the
 *   known kinds while still tolerating future ones (which parse via
 *   `LeadArtifactEntitySchema` but are NOT in this enum).
 */
export const KnownLeadArtifactKindSchema = z.enum([
  'request',
  'russia-request',
  'deal',
  'intake',
  // #2311: КП («CommercialProposal») linked to the Lead.
  'proposal',
]);
export type KnownLeadArtifactKind = z.infer<typeof KnownLeadArtifactKindSchema>;

/**
 * Narrow an open artifact `kind` to the closed known set. Returns `false`
 * for any future/unknown kind, so callers branching on the known kinds do
 * not have to catch a throwing enum parse.
 */
export function isKnownLeadArtifactKind(kind: string): kind is KnownLeadArtifactKind {
  return KnownLeadArtifactKindSchema.safeParse(kind).success;
}
